import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import API from '../../../services/API'
// MUI Imports
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined'
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import SanitizerOutlinedIcon from '@mui/icons-material/SanitizerOutlined'
import QRCodeReader from '../../../helpers/QRCodeReader'
import DocumentsField from '../../../helpers/DocumentsField'
import heic2any from 'heic2any'

// Mirrors FIT_OPTIONS on the mobile new/defect screen.
const FIT_OPTIONS = [
  { value: 'Yes', label: 'Yes', color: 'success' },
  { value: 'No', label: 'No', color: 'error' },
  { value: 'Unsure', label: "Don't know", color: 'warning' },
]

const SUMMARY_MAX = 50

const FieldHeading = ({ children, caption }) => (
  <Box mb={0.75}>
    <Typography variant="body2" fontWeight={600}>
      {children}
    </Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    )}
  </Box>
)

const NumberPlate = ({ children }) => (
  <Typography
    component="span"
    fontFamily="UKnumberplate"
    sx={{
      background: 'linear-gradient(to bottom, #f8d038 0%,#f5ca2e 100%)',
      color: '#111',
      px: 1,
      py: 0.25,
      borderRadius: 1,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Typography>
)

// A switch presented as a full-width row; its follow-up fields open inside
// the same outline so it is clear what they belong to.
const OptionRow = ({ icon, title, caption, checked, onChange, children }) => (
  <Box
    sx={{
      border: 1,
      borderColor: checked ? 'success.main' : 'divider',
      borderRadius: 2,
      transition: 'border-color 0.15s',
    }}
  >
    <Stack
      component="label"
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ px: 1.5, py: 1, cursor: 'pointer' }}
    >
      {icon}
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </Box>
      <Switch
        color="success"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
    </Stack>
    {children && (
      <Collapse in={checked} unmountOnExit>
        <Box px={1.5} pb={1.5} pt={0.5}>
          {children}
        </Box>
      </Collapse>
    )}
  </Box>
)

const DefectForm = ({ onSubmitted }) => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const presetAssetTag = queryParams.get('assetIDLabel')
  const [isVehicleDefect, setIsVehicleDefect] = useState(
    !!queryParams.get('vehicleID'),
  )
  const [hasAssetTag, setHasAssetTag] = useState(!!presetAssetTag)
  const [assetTag, setAssetTag] = useState(presetAssetTag ?? '')
  const [selectedVehicle, setSelectedVehicle] = useState(
    queryParams.get('vehicleID') ?? '',
  )
  const [vehicleList, setVehicleList] = useState([])
  const [fetchError, setFetchError] = useState(false)
  const [isIPC, setIsIPC] = useState(false)
  const [defectSummary, setDefectSummary] = useState('')
  const [defectDescription, setDefectDescription] = useState('')
  const [stillFitForUse, setStillFitForUse] = useState('')
  const [fitMissing, setFitMissing] = useState(false)
  const [images, setImages] = useState([])
  const [submitError, setSubmitError] = useState(false)
  const [imageUploadError, setImageUploadError] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [qrScannerOpen, setQrScannerOpen] = useState(false)
  const [assetOptions, setAssetOptions] = useState([])
  const [assetLookupLoading, setAssetLookupLoading] = useState(false)
  // Site Config → Fleet → Defect Alerts; on unless the org switched it off
  const [autoOutOfService, setAutoOutOfService] = useState(true)

  useEffect(() => {
    API.get('/defect-report-options', { skipGlobalErrorHandler: true })
      .then((res) => {
        if (res.status === 200) {
          setAutoOutOfService(res.data?.autoOutOfService !== false)
        }
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    API.get('/vehicleList')
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setVehicleList(
            [...res.data].sort(
              (a, b) =>
                (a.vehicleType || '').localeCompare(b.vehicleType || '') ||
                (a.callSign || '').localeCompare(b.callSign || ''),
            ),
          )
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
  }, [])

  // Live asset lookup so the tag stored on the defect is a real assetID -
  // the Defect model links to the asset record by that exact string.
  useEffect(() => {
    if (!hasAssetTag || assetTag.trim().length < 2) {
      setAssetOptions([])
      return undefined
    }
    setAssetLookupLoading(true)
    const timer = setTimeout(() => {
      API.get('/assets', { params: { q: assetTag.trim() } })
        .then((res) => {
          if (res.status === 200 && Array.isArray(res.data)) {
            setAssetOptions(res.data.slice(0, 10))
          }
        })
        .catch(() => {})
        .finally(() => setAssetLookupLoading(false))
    }, 300)
    return () => clearTimeout(timer)
  }, [assetTag, hasAssetTag])

  const vehicle = vehicleList.find((v) => v._id === selectedVehicle) ?? null
  // Fitness only matters when the report names a vehicle or a tagged asset
  const askFitForUse = isVehicleDefect || hasAssetTag
  const willGoOutOfService =
    autoOutOfService &&
    isVehicleDefect &&
    !!selectedVehicle &&
    stillFitForUse === 'No'

  // Uploads one image and resolves with its S3 key. The PUT is awaited and
  // its status checked (validateStatus never rejects) so a defect is never
  // saved pointing at an object that didn't make it into the bucket.
  const uploadImage = async (image) => {
    // TODO: Should this be done on the backend instead of sharing URL to frontend?
    // CONVERT IF IT'S A HEIC
    const isHeic =
      image.type === 'image/heic' ||
      image.type === 'image/heif' ||
      /\.heic$/i.test(image.name) ||
      /\.heif$/i.test(image.name)

    if (isHeic) {
      try {
        const converted = await heic2any({
          blob: image,
          toType: 'image/jpeg',
          quality: 0.85,
        })
        // heic2any may return Blob or Blob[]
        const jpegBlob = Array.isArray(converted) ? converted[0] : converted

        image = new File(
          [jpegBlob],
          image.name.replace(/\.(heic|heif)$/i, '.jpg'),
          {
            type: 'image/jpeg',
          },
        )
      } catch (err) {
        // heic2any rejects newer HEIC variants and .heic files that are
        // really JPEGs. Keep the evidence and upload the original as-is
        // (the mobile app already stores raw HEIC) rather than lose it.
        console.error('HEIC conversion failed - uploading original', err)
      }
    }

    // Some pickers hand over files with an empty type - fall back to the
    // file name so the key still gets an extension.
    const extension =
      image.type.split('/')[1] || image.name.split('.').pop().toLowerCase()

    const upload = await API.post('/gets3uploadurl', {
      folder: 'fleetequipment/defects',
      extension,
    })
    if (upload.status !== 200 || !upload.data?.url) {
      throw new Error('Unable to prepare the upload.')
    }

    // Phone photos on a poor signal can outlast the shared 60s axios
    // timeout, and an S3 error must surface here rather than in the global
    // handler.
    const put = await API.put(upload.data.url, image, {
      headers: {
        'Content-Type': image.type || 'application/octet-stream',
      },
      timeout: 0,
      skipGlobalErrorHandler: true,
    })
    if (put.status < 200 || put.status >= 300) {
      throw new Error(`Image upload failed (${put.status})`)
    }

    return upload.data.key
  }

  const handleAddNewSubmit = async (e, { withoutImages = false } = {}) => {
    e?.preventDefault()
    if (submitting) return
    // The toggle group has no native `required`, unlike the fields above.
    if (askFitForUse && !stillFitForUse) {
      setFitMissing(true)
      return
    }

    setSubmitting(true)
    setSubmitError(false)
    setImageUploadError(false)

    try {
      // One at a time: each presigned URL only lives for 60s, so it is
      // minted immediately before its own PUT rather than queueing behind
      // the browser's per-host connection limit.
      const imagePaths = []
      if (!withoutImages) {
        try {
          for (const image of images) {
            imagePaths.push(await uploadImage(image))
          }
        } catch (err) {
          console.error(err)
          setImageUploadError(true)
          return
        }
      }

      const res = await API.post('/defect', {
        vehicle: isVehicleDefect ? selectedVehicle : '',
        summary: defectSummary,
        description: defectDescription,
        isIPC,
        images: imagePaths,
        assetTag: hasAssetTag ? assetTag.trim() : '',
        stillFitForUse: askFitForUse ? stillFitForUse : '',
      })
      if (res.status === 200) {
        onSubmitted({
          defectNumber: res.data?.defectNumber,
          outOfServiceCallSign: res.data?.vehicleMarkedOutOfService
            ? (vehicle?.callSign ?? 'the vehicle')
            : null,
        })
      } else {
        setSubmitError(true)
      }
    } catch (err) {
      setSubmitError(true)
      console.log(err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Stack component="form" onSubmit={handleAddNewSubmit} spacing={2.5}>
      <Box>
        <FieldHeading caption="Switch on everything that applies.">
          What is affected?
        </FieldHeading>
        <Stack spacing={1}>
          <OptionRow
            icon={<DirectionsCarFilledOutlinedIcon color="action" />}
            title="This is a vehicle defect"
            caption="The fault is with a vehicle, or equipment fitted to it"
            checked={isVehicleDefect}
            onChange={setIsVehicleDefect}
          >
            <Autocomplete
              size="small"
              options={vehicleList}
              groupBy={(option) => option.vehicleType || 'Other'}
              getOptionLabel={(option) =>
                [option.callSign, option.regPlate].filter(Boolean).join(' · ')
              }
              isOptionEqualToValue={(a, b) => a._id === b._id}
              value={vehicle}
              onChange={(e, value) => setSelectedVehicle(value?._id ?? '')}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props
                return (
                  <li key={key} {...optionProps}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={700}>{option.callSign}</Typography>
                      {option.regPlate && (
                        <NumberPlate>{option.regPlate}</NumberPlate>
                      )}
                    </Stack>
                  </li>
                )
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vehicle"
                  required={isVehicleDefect}
                  placeholder="Search by call sign or registration…"
                  error={fetchError}
                  helperText={
                    fetchError
                      ? 'Unable to load the vehicle list. Refresh the page to try again.'
                      : undefined
                  }
                />
              )}
            />
          </OptionRow>

          <OptionRow
            icon={<MedicalServicesOutlinedIcon color="action" />}
            title="The defective equipment has an asset tag"
            caption="Links the defect to the equipment's record in the Asset Manager"
            checked={hasAssetTag}
            onChange={setHasAssetTag}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Autocomplete
                freeSolo
                fullWidth
                size="small"
                disabled={!!presetAssetTag}
                options={assetOptions}
                loading={assetLookupLoading}
                inputValue={assetTag}
                onInputChange={(e, value) => setAssetTag(value ?? '')}
                onChange={(e, value) =>
                  setAssetTag(
                    typeof value === 'string' ? value : (value?.assetID ?? ''),
                  )
                }
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : (option.assetID ?? '')
                }
                isOptionEqualToValue={(option, value) =>
                  option?._id === value?._id
                }
                filterOptions={(options) => options}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props
                  return (
                    <li key={key} {...optionProps}>
                      <Box display="flex" flexDirection="column" minWidth={0}>
                        <Typography fontWeight={700}>
                          {option.assetID}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[
                            [option.category, option.subCategory]
                              .filter(Boolean)
                              .join(' · '),
                            option.vehicle?.callSign
                              ? `on ${option.vehicle.callSign}`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' — ')}
                        </Typography>
                      </Box>
                    </li>
                  )
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Asset ID"
                    required={hasAssetTag}
                    placeholder="Type to search asset IDs…"
                    helperText="Type the ID printed on the tag, or scan its QR code"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {assetLookupLoading ? (
                            <CircularProgress size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {!presetAssetTag && (
                <Tooltip title="Scan the asset tag">
                  <IconButton
                    onClick={() => setQrScannerOpen(true)}
                    aria-label="Scan the asset tag"
                  >
                    <QrCodeScannerIcon />
                  </IconButton>
                </Tooltip>
              )}
              <QRCodeReader
                open={qrScannerOpen}
                onClose={() => setQrScannerOpen(false)}
                onScan={(result) => {
                  setAssetTag(result)
                  setQrScannerOpen(false)
                }}
              />
            </Stack>
          </OptionRow>

          <OptionRow
            icon={<SanitizerOutlinedIcon color="action" />}
            title="This defect affects Infection Prevention and Control"
            caption="For example torn upholstery, damaged surfaces or anything that cannot be cleaned properly"
            checked={isIPC}
            onChange={setIsIPC}
          />
        </Stack>
      </Box>

      <TextField
        label="Summary"
        fullWidth
        required
        value={defectSummary}
        onChange={(e) => setDefectSummary(e.target.value)}
        placeholder="A short title, e.g. Nearside rear tyre worn"
        helperText={`${defectSummary.length}/${SUMMARY_MAX}`}
        FormHelperTextProps={{ sx: { textAlign: 'right' } }}
        inputProps={{ maxLength: SUMMARY_MAX }}
      />
      <TextField
        minRows={4}
        multiline
        required
        fullWidth
        label="Description of the Defect"
        value={defectDescription}
        onChange={(e) => setDefectDescription(e.target.value)}
        placeholder="What is wrong, where it is, and anything the fleet team should know"
      />

      <Collapse in={askFitForUse} unmountOnExit>
        <FieldHeading>
          Is the vehicle or medical device still fit for use? *
        </FieldHeading>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={stillFitForUse}
          onChange={(e, value) => {
            if (!value) return
            setStillFitForUse(value)
            setFitMissing(false)
          }}
        >
          {FIT_OPTIONS.map((option) => (
            <ToggleButton
              key={option.value}
              value={option.value}
              color={option.color}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {fitMissing && (
          <Typography variant="caption" color="error">
            Choose whether it is still fit for use.
          </Typography>
        )}
        {willGoOutOfService && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Submitting will mark {vehicle?.callSign ?? 'this vehicle'} as Out
            of Service.
          </Alert>
        )}
      </Collapse>

      <Box>
        <FieldHeading>Photos</FieldHeading>
        <DocumentsField
          files={images}
          onFilesChange={setImages}
          disabled={submitting}
          accept="image/*,.heic,.heif"
          acceptLabel="image files"
          caption="Optional — photos of the defect help the fleet team act on it"
          pendingLabel="Uploads when you submit"
        />
      </Box>

      {imageUploadError && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={submitting}
              onClick={(e) =>
                e.currentTarget.form?.reportValidity() &&
                handleAddNewSubmit(null, { withoutImages: true })
              }
            >
              Submit without photos
            </Button>
          }
        >
          Your photos could not be uploaded, so the defect has not been
          submitted yet. Check your connection and press Report defect to try
          again.
        </Alert>
      )}
      {submitError && (
        <Alert severity="error">
          Unable to submit this form. Please try again later.
        </Alert>
      )}

      <Stack direction="row" justifyContent="flex-end">
        <Button
          type="submit"
          variant="contained"
          color="success"
          disabled={submitting}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {submitting
            ? images.length > 0
              ? 'Uploading…'
              : 'Submitting…'
            : 'Report defect'}
        </Button>
      </Stack>
    </Stack>
  )
}

// Crew-facing page for reporting a vehicle or equipment defect (header +
// menu, dashboard, search, and the vehicle / asset pages via query params).
const AddNewDefect = () => {
  const navigate = useNavigate()
  const [submitted, setSubmitted] = useState(null)
  const [formKey, setFormKey] = useState(0)

  return (
    <Box maxWidth={760} mx="auto">
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={2.5}>
          <BuildCircleOutlinedIcon color="error" />
          <Box>
            <Typography variant="h5" fontWeight={800}>
              Report a Defect
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tell the fleet team about a fault with a vehicle or a piece of
              equipment.
            </Typography>
          </Box>
        </Stack>
        {submitted ? (
          <Stack spacing={2} alignItems="center" py={4} textAlign="center">
            <CheckCircleIcon color="success" sx={{ fontSize: 56 }} />
            <Typography variant="h6" fontWeight={800}>
              Defect reported
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {submitted.defectNumber
                ? `Defect #${submitted.defectNumber} has been logged`
                : 'The defect has been logged'}
              {submitted.outOfServiceCallSign
                ? ` and ${submitted.outOfServiceCallSign} has been marked Out of Service.`
                : ' for the fleet team to review.'}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => {
                  setSubmitted(null)
                  setFormKey((k) => k + 1)
                }}
              >
                Report another
              </Button>
              <Button variant="contained" onClick={() => navigate('/')}>
                Back to dashboard
              </Button>
            </Stack>
          </Stack>
        ) : (
          <DefectForm key={formKey} onSubmitted={setSubmitted} />
        )}
      </Paper>
    </Box>
  )
}

export default AddNewDefect
